import {
  Box,
  Slide,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";

import { ArtifactStyle } from "../verification/document/artifactResponse/VerficationArtifactsStyles";
import { useVerification } from "../../../context/Verification.context";
import SurveyResponse from "../verification/document/artifactResponse/SurveyResponse";
import ToggleComponent from "./Toggle.component";
import PaginationComponent from "../verification/document/artifactResponse/Pagination.component";
import AppointmentResponse from "../verification/document/artifactResponse/AppointmentResponse";
import VideoPlayer from "../admin/dealership/tabs/VideoPlayer.component";
import DealerAppResponse from "../verification/document/artifactResponse/DealerAppResponse";
import TradeAndQuoteComponent from "../../../utils/TradeAndQuote.component";
import ReviewResponse from "../verification/document/artifactResponse/ReviewResponse";
import RatingResponse from "../verification/document/artifactResponse/RatingResponse";
import VehicleTransactionResponse from "../verification/document/artifactResponse/VehicleTransactionResponse";
import WarrantyPresentation from "../verification/document/warranty/WarrantyPresentation.component";
import CustomControlsVideoPlayer from "./CustomControlsVideoPlayer";
import ServiceMaintenance from "../admin/dealership/tabs/ServiceMaintenance.component";
import { FETCH_VEHICLE_TRANSACTION } from "../../../graphql/mutation/mutation";
import { useMutation } from "@apollo/client";
import { normalizePlaybackUrl, getWalkthroughEmbed } from "../../../utils/videoPlayer";
import AppButton from "../../base/AppButton.component";

// Returns true only when we can trust that `video.duration` is the real,
// final duration (not a short/provisional value while more of the file is
// still buffering) AND `currentTime` is truly within `tolerance` seconds
// of it.
//
// Why this matters: on some browsers (iOS Safari in particular, and any
// progressively-streamed source) `video.duration` can briefly report a
// shorter value than the actual media length while only part of the file
// has been fetched, then jump upward later via a `durationchange` event.
// A naive `duration - currentTime <= 1` check can fire while `duration`
// is still that stale short value — which was cutting the avatar off
// mid-sentence and popping the response overlay several seconds early.
const isReliablyNearEnd = (video, tolerance = 0.25) => {
  if (!video) return false;
  const { duration, currentTime, buffered } = video;
  if (!duration || !isFinite(duration) || duration <= 0) return false;
  if (duration - currentTime > tolerance) return false;

  // Guard against a stale/short `duration` reading: only trust it once the
  // buffered range actually extends up to (near) that duration. If the
  // browser hasn't buffered that far yet, `duration` may still grow later
  // and we'd be treating "near end" as true way too early.
  try {
    if (buffered && buffered.length > 0) {
      const bufferedEnd = buffered.end(buffered.length - 1);
      if (bufferedEnd < duration - 0.5) return false;
    }
  } catch {
    // buffered can throw in some edge cases (e.g. no source yet); fail
    // safe by not blocking on it.
  }

  return true;
};

const FetchAIVideoUrl = (args) => {
  const {
    artifact,
    manageArtifactResponse,
    setSurveyScreen,
    setAppointmentScreen,
    setDealerAppScreen,
    handleClose,
    videoUrl,
    currentIndex,
    totalPages,
    handleNext,
    handlePrevious,
    isAI,
    animateIn,
    setAnimateIn,
    showSurveyButton,
    setShowSurveyButton,
    duration,
    setDuration,
    direction,
    pagination = true,
    walkthrough = false,
    isClose = true,
    isSpokespersonActive,
    isQuestionAnswered,
    Theme,
    setVideoScreen,
    setRefferalScreen,
    setReviewScreen,
    referralScreen,
    reviewScreen,
    tradeVehicleIds,
    setTradeVehicleIds,
    tradeVehicles,
    getVehicleDetails,
    dialog,
    setVehicleTransactionScreen,
    onMainVideoEnded,
    onStepInteraction,
  } = args;

  const [fetchVehicle] = useMutation(FETCH_VEHICLE_TRANSACTION);
  const verification = useVerification();
  const videoRef = useRef(null);
  const overlayLatchedRef = useRef(false);
  const [loading, setIsLoading] = useState(true);
  const [selectedUrl, setSelectedUrl] = useState(null);
  const [videoReady, setVideoReady] = useState(false);
  const [walkthroughEmbed, setWalkthroughEmbed] = useState(null);
  const [overlayLatched, setOverlayLatched] = useState(false);
  const [keyboardOffset, setKeyboardOffset] = useState(0);

  const revealOverlay = useCallback(() => {
    overlayLatchedRef.current = true;
    setOverlayLatched(true);
    setShowSurveyButton?.(true);
  }, [setShowSurveyButton]);

  const resetOverlayLatch = useCallback(() => {
    overlayLatchedRef.current = false;
    setOverlayLatched(false);
  }, []);

  const handleStepInteraction = useCallback(
    (options) => {
      if (options?.hideOverlay) {
        resetOverlayLatch();
        setShowSurveyButton?.(false);
      } else {
        const video = videoRef.current;
        const nearEnd = Boolean(
          video && (video.ended || isReliablyNearEnd(video)),
        );

        if (overlayLatchedRef.current || nearEnd) {
          revealOverlay();
        }
      }
      onStepInteraction?.();
    },
    [onStepInteraction, revealOverlay, resetOverlayLatch, setShowSurveyButton],
  );

  const isSurveyOtherWithMicVisible =
    artifact?.question &&
    verification?.artifactsResponse?.surveyResponse?.some(
      (item) => item?.question_id === artifact?.id && item?.isOther,
    ) &&
    Boolean(
      verification?.aiData?.instructionVideoCompletedByArtifact?.[artifact?.id],
    );

  const isSurveyOtherInstructionVideo =
    artifact?.question &&
    verification?.artifactsResponse?.surveyResponse?.some(
      (item) => item?.question_id === artifact?.id && item?.isOther,
    );

  const markSurveyOtherInstructionComplete = useCallback(() => {
    if (!isSurveyOtherInstructionVideo) return;

    verification?.setAIData?.((prev) => {
      if (prev?.instructionVideoCompletedByArtifact?.[artifact?.id]) {
        return prev;
      }

      return {
        ...prev,
        instructionVideoCompletedByArtifact: {
          ...prev?.instructionVideoCompletedByArtifact,
          [artifact?.id]: true,
        },
      };
    });
  }, [
    artifact?.id,
    isSurveyOtherInstructionVideo,
    verification,
  ]);

  const handleMainVideoEnded = useCallback(() => {
    markSurveyOtherInstructionComplete();

    if (artifact?.__typename === "VehicleTransactionArtifact") {
      setVehicleTransactionScreen?.(true);
    }

    revealOverlay();
    onMainVideoEnded?.();
  }, [
    artifact?.__typename,
    markSurveyOtherInstructionComplete,
    onMainVideoEnded,
    revealOverlay,
    setVehicleTransactionScreen,
  ]);

  const shouldHoldAtEnd =
    isSurveyOtherWithMicVisible ||
    (overlayLatched && !isSurveyOtherInstructionVideo);

  useEffect(() => {
    let selectedUrl = null;
    let embed = null;

    const getOther = verification?.artifactsResponse?.surveyResponse?.find(
      (item) => item?.question_id === artifact?.id && item?.isOther,
    );

    const findOther = verification?.tradeQuote?.find(
      (item) => item?.typeName === "surveyOther",
    );

    if (walkthrough) {
      const rawUrl = !verification.aiData.spanish
        ? videoUrl?.english_url
        : videoUrl?.spanish_url;
      embed = getWalkthroughEmbed(rawUrl);
      selectedUrl = embed?.type === "direct" ? embed.src : null;
    } else if (getOther && findOther) {
      selectedUrl = !verification.aiData.spanish
        ? normalizePlaybackUrl(findOther?.ai_url?.english_url)
        : normalizePlaybackUrl(findOther?.ai_url?.spanish_url);
    } else if (artifact?.__typename === "ReviewUrl") {
      selectedUrl = !verification.aiData.spanish
        ? normalizePlaybackUrl(artifact?.ai_url?.english_url)
        : normalizePlaybackUrl(artifact?.ai_url?.spanish_url);
    } else if (
      (artifact?.review_question || artifact?.es_review_question) &&
      verification?.artifactsResponse?.reviewResponse?.answer === "yes"
    ) {
      selectedUrl = !verification.aiData.spanish
        ? normalizePlaybackUrl(artifact?.referral_ai_url?.english_url)
        : normalizePlaybackUrl(artifact?.referral_ai_url?.spanish_url);
    } else {
      selectedUrl = !verification.aiData.spanish
        ? normalizePlaybackUrl(videoUrl?.english_url) ||
          normalizePlaybackUrl(artifact?.url)
        : normalizePlaybackUrl(videoUrl?.spanish_url) ||
          normalizePlaybackUrl(artifact?.es_url);
    }

    if (
      selectedUrl === "null" ||
      selectedUrl === "" ||
      selectedUrl === undefined
    ) {
      selectedUrl = null;
    }

    setSelectedUrl(selectedUrl);
    setWalkthroughEmbed(embed);
  }, [
    verification.aiData.spanish,
    videoUrl,
    walkthrough,
    verification?.artifactsResponse?.reviewResponse,
    reviewScreen,
    artifact,
    verification?.artifactsResponse?.surveyResponse,
  ]);

  useEffect(() => {
    const surveyResponses =
      verification?.artifactsResponse?.surveyResponse || [];

    const getOther = surveyResponses.find(
      (item) => item?.question_id === artifact?.id && item?.isOther,
    );

    verification.setAIData((prev) => ({
      ...prev,
      isOther: getOther?.isOther,
    }));
  }, [verification?.artifactsResponse?.surveyResponse]);

  useEffect(() => {
    resetOverlayLatch();
  }, [artifact?.id, resetOverlayLatch]);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;

    const syncKeyboardOffset = () => {
      const offset = Math.max(
        0,
        window.innerHeight - viewport.height - viewport.offsetTop,
      );
      setKeyboardOffset(offset);
    };

    syncKeyboardOffset();
    viewport.addEventListener("resize", syncKeyboardOffset);
    viewport.addEventListener("scroll", syncKeyboardOffset);

    return () => {
      viewport.removeEventListener("resize", syncKeyboardOffset);
      viewport.removeEventListener("scroll", syncKeyboardOffset);
    };
  }, []);

  useEffect(() => {
    if (selectedUrl) {
      resetOverlayLatch();
      setShowSurveyButton?.(false);
    }

    setVideoReady(false);
    const video = videoRef.current;
    const handleLoadedMetadata = () => {
      setIsLoading(false);
      setDuration(video.duration);
      setVideoReady(true);
    };
    const handleCanPlay = () => {
      setAnimateIn(true);
    };
    video?.addEventListener("loadedmetadata", handleLoadedMetadata);
    video?.addEventListener("canplay", handleCanPlay);
    return () => {
      video?.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video?.removeEventListener("canplay", handleCanPlay);
    };
  }, [selectedUrl, resetOverlayLatch, setShowSurveyButton]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const syncOverlayWithPlayback = () => {
      if (video.ended || isReliablyNearEnd(video)) {
        markSurveyOtherInstructionComplete();
        revealOverlay();
      } else {
        // We're genuinely not at/near the end anymore — most commonly
        // because the user rewound the scrubber to replay the clip.
        // Un-latch here (not just hide the button) so `holdAtEnd` stops
        // forcing playback back to the last frame. Without this, the
        // latch only ever cleared on navigating to a different slide,
        // so pressing Play after rewinding the same slide would get
        // immediately paused and snapped back to the end.
        resetOverlayLatch();
        setShowSurveyButton?.(false);
      }
    };

    video.addEventListener("timeupdate", syncOverlayWithPlayback);
    video.addEventListener("ended", handleMainVideoEnded);
    video.addEventListener("seeked", syncOverlayWithPlayback);
    // `duration` can be revised upward after more of the file buffers
    // (especially on iOS Safari, or any progressively-streamed source).
    // Re-run the check whenever that happens so a stale, too-short
    // duration reading can't leave the overlay latched prematurely.
    video.addEventListener("durationchange", syncOverlayWithPlayback);

    return () => {
      video.removeEventListener("timeupdate", syncOverlayWithPlayback);
      video.removeEventListener("ended", handleMainVideoEnded);
      video.removeEventListener("seeked", syncOverlayWithPlayback);
      video.removeEventListener("durationchange", syncOverlayWithPlayback);
    };
  }, [
    duration,
    selectedUrl,
    handleMainVideoEnded,
    markSurveyOtherInstructionComplete,
    revealOverlay,
    setShowSurveyButton,
  ]);

  // Warranty: render directly, no video flow needed
  if (artifact?.__typename === "WarrantyArtifact") {
    return (
      <Box sx={ArtifactStyle.mainBox}>
        <Box sx={ArtifactStyle.toggleZone}>
          <ToggleComponent
            isAI={isAI}
            handleClose={handleClose}
            walkthrough={walkthrough}
            isClose={isClose}
          />
        </Box>
        <Box sx={ArtifactStyle.contentBox}>
          <WarrantyPresentation
            isPortraitMobile={false}
            onDismiss={handleNext}
          />
        </Box>
        {pagination && (
          <Box sx={ArtifactStyle.paginationZone}>
            <PaginationComponent
              currentIndex={currentIndex}
              totalPages={totalPages}
              handleNext={handleNext}
              handlePrevious={handlePrevious}
            />
          </Box>
        )}
      </Box>
    );
  }

  const isWalkthroughEmbedPlayable =
    walkthrough && walkthroughEmbed && walkthroughEmbed.type !== "direct";
  const hasPlayableContent = Boolean(selectedUrl) || isWalkthroughEmbedPlayable;

  return (
    <Box sx={ArtifactStyle.mainBox}>
      <Box sx={ArtifactStyle.toggleZone}>
        <ToggleComponent
          isAI={isAI}
          handleClose={handleClose}
          walkthrough={walkthrough}
          isClose={isClose}
        />
      </Box>

      {/* Zone 2 — Video */}
      <Box sx={ArtifactStyle.contentBox}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            overflow: "hidden",
          }}
        >
          {hasPlayableContent ? (
            <Slide
              in={true}
              key={currentIndex}
              direction={direction}
              mountOnEnter
              unmountOnExit
              timeout={{ enter: 500, exit: 500 }}
            >
              <Box
                sx={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  top: 0,
                  left: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  overflow: "hidden", // prevent bleed from escaping
                  bgcolor: "#fff",
                }}
              >
                {isWalkthroughEmbedPlayable ? (
                  <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                    <iframe
                      key={walkthroughEmbed.src}
                      src={walkthroughEmbed.src}
                      title="Walkthrough video"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        border: 0,
                      }}
                      onLoad={() => {
                        setIsLoading(false);
                        setAnimateIn(true);
                      }}
                    />
                  </Box>
                ) : artifact?.title || artifact?.es_title ? (
                  <VideoPlayer
                    selectedUrl={selectedUrl}
                    artifact={artifact}
                    setVideoScreen={setVideoScreen}
                    setIsLoading={setIsLoading}
                    setAnimateIn={setAnimateIn}
                    isAIVideo={true}
                    onVideoEnded={handleMainVideoEnded}
                  />
                ) : (
                  <CustomControlsVideoPlayer
                    videoRef={videoRef}
                    src={selectedUrl}
                    animateIn={animateIn}
                    holdAtEnd={shouldHoldAtEnd}
                    onEnded={handleMainVideoEnded}
                    onReady={() => {
                      setIsLoading(false);
                      setAnimateIn(true);
                    }}
                    onDuration={(d) => setDuration?.(d)}
                    // Disable bleed trick for walkthrough so the video
                    // centres correctly inside contentBox on mobile
                    widenPercent={walkthrough ? 0 : 0}
                  />
                )}
              </Box>
            </Slide>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <Typography variant="h6" textAlign="center">
                {verification.aiData.spanish
                  ? "No Spanish video available"
                  : "No English video available"}
              </Typography>
            </Box>
          )}

          {/* Responses overlay */}
          {(showSurveyButton || isSurveyOtherWithMicVisible) && (
            <Box
              sx={{
                ...ArtifactStyle.videoResponseOverlay,
                // bottom: {
                //   xs: 56 + keyboardOffset,
                //   sm: 60 + keyboardOffset,
                //   md: 64 + keyboardOffset,
                // },
                opacity: showSurveyButton || isSurveyOtherWithMicVisible ? 1 : 0,
              }}
            >
              {(artifact?.question ||
                artifact?.es_question ||
                artifact?.artifact_type_id === 2 ||
                artifact?.__typename === "SurveyArtifact") && (
                <SurveyResponse
                  isAISurvey={true}
                  artifact={artifact}
                  manageArtifactResponse={manageArtifactResponse}
                  setSurveyScreen={setSurveyScreen}
                  onStepInteraction={handleStepInteraction}
                />
              )}

              {artifact?.__typename === "VehicleTransactionArtifact" && (
                <VehicleTransactionResponse
                  artifact={artifact}
                  isPopup={true}
                  handleNext={handleNext}
                  handleClose={handleClose}
                  setVehicleTransactionScreen={setVehicleTransactionScreen}
                  onInteraction={handleStepInteraction}
                  onSubmit={async () => {
                    const vtr = verification?.artifactsResponse?.vehicleTransactionResponse;
                    if (vtr) {
                      const response = await fetchVehicle({
                        variables: {
                          fetchVehicleTransactionInput: {
                            vehicleTransactionResponse: {
                              id: vtr?.id || 0,
                              vehicle_id: vtr?.vehicle_id || 0,
                              artifact_id: artifact?.id,
                              dealership_id: parseInt(verification?.dealershipId),
                              employee_user_id: parseInt(verification?.employeeId),
                              user_id: parseInt(verification?.customer?.userId),
                              vehicle_type: vtr?.vehicle_type,
                              transaction_type: vtr?.transaction_type,
                              year: vtr?.year,
                              make: vtr?.make,
                              model: vtr?.model,
                              created_by: parseInt(verification?.employeeId),
                              updated_by: parseInt(verification?.employeeId),
                            },
                          },
                        },
                      });

                      if (response?.data?.fetchVehicle?.vehicleTransactionResponse) {
                        const savedIds = response.data.fetchVehicle.vehicleTransactionResponse;
                        verification.setArtifactsResponse((prev) => ({
                          ...prev,
                          vehicleTransactionResponse: {
                            ...prev.vehicleTransactionResponse,
                            id: savedIds.id,
                            vehicle_id: savedIds.vehicle_id,
                          },
                        }));
                      }
                      return response;
                    }
                  }}
                />
              )}

              {((artifact?.message && artifact?.preference_options?.length > 0) ||
                (artifact?.es_message && artifact?.es_preference_options?.length > 0) ||
                artifact?.artifact_type_id === 6 ||
                artifact?.__typename === "AppointmentArtifact") && (
                <AppointmentResponse
                  artifact={artifact}
                  setAppointmentScreen={setAppointmentScreen}
                  isAIAppointment={true}
                  onInteraction={handleStepInteraction}
                />
              )}

              {artifact?.__typename === "DealerAppUrl" && (
                <DealerAppResponse
                  artifact={artifact}
                  setDealerAppScreen={setDealerAppScreen}
                  isQuestionAnswered={isQuestionAnswered}
                  Theme={Theme}
                  isAIDealerApp={true}
                  onInteraction={handleStepInteraction}
                />
              )}

              {(artifact?.review_question ||
                artifact?.es_review_question ||
                artifact?.referral_question ||
                artifact?.es_referral_question ||
                artifact?.artifact_type_id === 7) && (
                <ReviewResponse
                  artifact={artifact}
                  handleClose={handleClose}
                  videoUrl={videoUrl}
                  isAIReview={true}
                  setReviewScreen={setReviewScreen}
                  setRefferalScreen={setRefferalScreen}
                  onStepInteraction={handleStepInteraction}
                />
              )}

              {(artifact?.artifact_type_id === 15 ||
                artifact?.__typename === "ReviewUrl") && (
                <RatingResponse
                  artifact={artifact}
                  handleClose={handleClose}
                  videoUrl={videoUrl}
                  isAIReview={true}
                  setReviewScreen={setReviewScreen}
                  setRefferalScreen={setRefferalScreen}
                  onInteraction={handleStepInteraction}
                />
              )}

              {((artifact?.followup_message_1 || artifact?.es_followup_message_1) &&
                (artifact?.followup_message_2 || artifact?.es_followup_message_2) &&
                (artifact?.followup_message_3 || artifact?.es_followup_message_3)) ||
              artifact?.artifact_type_id === 16 ||
              artifact?.__typename === "ServiceMaintenanceArtifact" ? (
                <ServiceMaintenance
                  artifact={artifact}
                  handleClose={handleClose}
                  videoUrl={videoUrl}
                  isAIServiceMaintenance={true}
                />
              ) : null}

              {artifact?.__typename === "TradeArtifact" && (
                <TradeAndQuoteComponent
                  artifact={artifact}
                  handleClose={handleClose}
                  videoUrl={videoUrl}
                  isAITrade={true}
                  loading={loading}
                  tradeVehicleIds={tradeVehicleIds}
                  setTradeVehicleIds={setTradeVehicleIds}
                  tradeVehicles={tradeVehicles}
                  getVehicleDetails={getVehicleDetails}
                  onInteraction={handleStepInteraction}
                />
              )}

              {artifact?.__typename === "QuoteArtifact" && (
                <TradeAndQuoteComponent
                  artifact={artifact}
                  handleClose={handleClose}
                  videoUrl={videoUrl}
                  isAIQuote={true}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Zone 3 — Pagination / Next */}
      {walkthrough && typeof handleNext === "function" ? (
        <Box
          sx={{
            ...ArtifactStyle.paginationZone,
            display: "flex",
            justifyContent: "center",
            pt: 1,
          }}
        >
          <AppButton
            buttonText="NEXT"
            size="medium"
            variant="contained"
            sx={{ width: { xs: "70%", sm: "50%" } }}
            clickHandler={handleNext}
          />
        </Box>
      ) : (
        pagination && (
          <Box sx={ArtifactStyle.paginationZone}>
            <PaginationComponent
              currentIndex={currentIndex}
              totalPages={totalPages}
              handleNext={handleNext}
              handlePrevious={handlePrevious}
            />
          </Box>
        )
      )}
    </Box>
  );
};

export default FetchAIVideoUrl;